import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import { ABOUT_STORY, ROADMASTER_STORY, OWNER } from "@/data/site";

export default function About() {
  const paragraphs = ABOUT_STORY.split("\n\n");
  const roadmasterParas = ROADMASTER_STORY.split("\n\n");
  return (
    <div data-testid="about-page">
      <section className="parchment border-b border-[color:var(--accent)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-end">
          <Reveal className="md:col-span-7">
            <div className="eyebrow mb-6">About · The Caretaker</div>
            <h1 className="font-display text-6xl md:text-8xl tracking-tighter leading-[0.92]">
              A hobby that began with a <span className="italic text-[color:var(--brg)]">Roadmaster.</span>
            </h1>
          </Reveal>
          <Reveal className="md:col-span-5" delay={150}>
            <div className="tile aspect-[4/5]">
              <img
                src="https://www.britcars.us/old/Previously%20Owned/54BuickRoadmaster250.jpg"
                alt="1954 Buick Roadmaster Hardtop — the spark"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="mt-3 text-xs uppercase tracking-[0.22em] text-[color:var(--ink-muted)] text-center">
              1954 Buick Roadmaster — the car that started it all
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[color:var(--surface)] border-b border-[color:var(--accent)]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-12 items-start">
              <div className="md:col-span-3">
                <div className="eyebrow mb-3">Prologue</div>
                <div className="font-display italic text-3xl text-[color:var(--brg)]">The Roadmaster</div>
                <div className="hairline mt-6 w-16"></div>
                <p className="mt-6 text-sm text-[color:var(--ink-muted)] leading-relaxed max-w-xs">
                  A 1954 Buick, a Californian schoolteacher&apos;s hidden history, and the trophies that led — quietly — to British cars.
                </p>
              </div>
              <div className="md:col-span-9 space-y-8">
                {roadmasterParas.map((p, i) => (
                  <p
                    key={i}
                    className={
                      i === 0
                        ? "font-display text-3xl md:text-4xl leading-[1.2] text-[color:var(--ink)]"
                        : i === roadmasterParas.length - 1
                          ? "font-display italic text-xl md:text-2xl leading-snug text-[color:var(--brg)] pt-6 border-t border-[color:var(--accent)]"
                          : "text-lg md:text-xl leading-relaxed text-[color:var(--ink-secondary)]"
                    }
                  >
                    {p}
                  </p>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-20 grid md:grid-cols-3 gap-6">
              {[
                { stat: "34", label: "Years owned by one schoolteacher" },
                { stat: "136,000", label: "Actual miles on the clock" },
                { stat: "Dozens", label: "Of trophies at car shows" },
              ].map((s) => (
                <div key={s.label} className="border-t border-[color:var(--accent)] pt-6">
                  <div className="font-display text-5xl md:text-6xl tracking-tighter text-[color:var(--brg)]">{s.stat}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-[color:var(--ink-muted)]">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="parchment">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-3">
              <div className="eyebrow mb-3">Chapter I</div>
              <div className="font-display italic text-3xl text-[color:var(--brg)]">The Spark</div>
            </Reveal>
            <Reveal className="md:col-span-9" delay={100}>
              <p className="font-display text-2xl md:text-3xl leading-snug text-[color:var(--ink)]">
                {paragraphs[0]}
              </p>
            </Reveal>
          </div>

          <div className="hairline my-20"></div>

          <div className="grid md:grid-cols-12 gap-12">
            <Reveal className="md:col-span-3">
              <div className="eyebrow mb-3">Chapter II</div>
              <div className="font-display italic text-3xl text-[color:var(--brg)]">The Calling</div>
            </Reveal>
            <Reveal className="md:col-span-9" delay={100}>
              <p className="font-display text-2xl md:text-3xl leading-snug">
                {paragraphs[1]}
              </p>
            </Reveal>
          </div>

          <div className="hairline my-20"></div>

          <Reveal>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { stat: "40+", label: "Years collecting" },
                { stat: "11", label: "Cars currently in the garage" },
                { stat: "∞", label: "Sunday morning donuts" },
              ].map((s) => (
                <div key={s.label} className="border-t border-[color:var(--accent)] pt-6">
                  <div className="font-display text-6xl tracking-tighter text-[color:var(--brg)]">{s.stat}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-[color:var(--ink-muted)]">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="parchment border-t border-[color:var(--accent)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Reveal>
            <div className="ornament text-3xl mb-6">✦</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto">
              Looking for drivers who can <span className="italic text-[color:var(--brg)]">work a manual.</span>
            </h2>
            <p className="mt-8 text-lg text-[color:var(--ink-secondary)] max-w-2xl mx-auto">
              If you can heel‑and‑toe, double‑clutch, or simply find third without looking, write to {OWNER.name} below.
            </p>
            <div className="mt-10">
              <Link to="/contact" data-testid="about-contact-cta" className="btn-primary">
                Write to {OWNER.name}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
